import { useState, useEffect } from "react";
import { History, ShieldAlert, Globe, Monitor } from "lucide-react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";

export default function VerificationLogs() {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchLogs();
    }, []);

    const fetchLogs = async () => {
        try {
            const r = await api.get("/documents/verification-logs");
            setLogs(r.data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">Verification Logs</h1>
                <p className="text-sm text-muted-foreground">Monitor public access logs for generated certificates and letters.</p>
            </div>

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            ) : logs.length === 0 ? (
                <div className="glass py-16 text-center rounded-2xl border border-slate-200 dark:border-white/10">
                    <p className="text-muted-foreground text-sm">No verification scans recorded yet.</p>
                </div>
            ) : (
                <div className="glass overflow-hidden rounded-2xl border border-slate-200 dark:border-white/10 shadow-lg bg-white/50 dark:bg-[hsl(var(--surface))]/40">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 dark:bg-slate-800/40 text-xs font-semibold text-muted-foreground border-b border-slate-200 dark:border-white/10">
                                    <th className="px-6 py-4">Certificate ID</th>
                                    <th className="px-6 py-4">IP Address</th>
                                    <th className="px-6 py-4">User Agent</th>
                                    <th className="px-6 py-4">Verification Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-white/10 text-sm text-slate-700 dark:text-slate-300">
                                {logs.map((log) => (
                                    <tr key={log.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition">
                                        <td className="px-6 py-4 font-semibold text-primary">{log.certificateId}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-1.5 font-mono text-xs">
                                                <Globe className="w-3.5 h-3.5 text-muted-foreground" />
                                                <span>{log.ipAddress}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 max-w-xs truncate text-xs" title={log.userAgent}>
                                            <div className="flex items-center gap-1.5">
                                                <Monitor className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                                                <span className="truncate">{log.userAgent}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-xs text-muted-foreground">
                                            {new Date(log.verifiedAt).toLocaleString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
